import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Card, CardContent } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import img from "../assets/qural1.png";
import LockIcon from '@mui/icons-material/Lock';
import { makeApiRequest } from '../utils/api';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const otp = location.state?.otp || '';

  useEffect(() => {
    if (!email || !otp) {
      navigate('/forgot-password');
    }
  }, [email, otp, navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!password || !passwordConfirmation) {
      setError('Both fields are required.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const response = await makeApiRequest('post', '/api/forgot-password/reset', {
        email,
        otp,
        password,
        password_confirmation: passwordConfirmation
      });
      if (response.status === 'success') {
        setSuccess('Password reset successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.message || 'Failed to reset password.');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      <Box
        sx={{
          width: '100%',
          height: '60vh',
          background: 'linear-gradient(to right, #2353A3 0%, #59C8F2 50%, #FFFFFF 100%)',
        }}
      />
      <Box
        sx={{
          width: 0,
          height: 0,
          borderLeft: '100vw solid transparent',
          borderBottom: '13.9vw solid #ffffff',
          marginTop: '-13.8vw',
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '80%', sm: '50%', md: "35%" },
          padding: '20px',
          backgroundColor: 'transparent',
          zIndex: 10,
        }}
      >
        <Card sx={{ borderRadius: '8px', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}>
          <CardContent>
            <Typography variant="h5" component="h2" sx={{ marginBottom: '30px', color: '#333', textAlign: 'center' }}>
              <img style={{ height: "122px", objectFit: 'contain', width: '100%' }} src={img} alt="Logo" />
            </Typography>
            <Typography variant="h6" sx={{ marginBottom: '20px', textAlign: 'center', fontWeight: 'bold' }}>
              Reset Password
            </Typography>
            {error && (
              <Typography variant="body2" color="error" sx={{ marginBottom: 2, textAlign: 'center' }}>
                {error}
              </Typography>
            )}
            {success && (
              <Typography variant="body2" color="primary" sx={{ marginBottom: 2, textAlign: 'center', color: 'green' }}>
                {success}
              </Typography>
            )}
            <Box component="form" onSubmit={handleResetPassword} noValidate>
              <Box sx={{ marginBottom: 3 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  placeholder="Enter new password"
                  variant="outlined"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: <LockIcon />,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ marginBottom: 4 }}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  placeholder="Confirm new password"
                  variant="outlined"
                  required
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  InputProps={{
                    endAdornment: <LockIcon />,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !!success}
                sx={{
                  backgroundColor: '#007BFF',
                  '&:hover': { backgroundColor: '#0056b3' },
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default ResetPassword;
